import { useState, useRef, useEffect, useCallback } from 'react'
import { Text } from '@/components/ui/Text'

// ---------------------------------------------------------------------------
// Shared Divider
// ---------------------------------------------------------------------------

export function SidebarDivider() {
  return <div className="h-px w-full bg-twin-chat-divider/20" />
}

// ---------------------------------------------------------------------------
// Shared CollapsibleText — configurable line clamp with expand/collapse
// ---------------------------------------------------------------------------

export interface CollapsibleTextProps {
  label?: string
  text: string
  maxLines?: number
}

export function CollapsibleText(props: CollapsibleTextProps) {
  const { label, text, maxLines = 8 } = props

  const textRef = useRef<HTMLElement>(null)
  const [isClamped, setIsClamped] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const checkClamp = useCallback(() => {
    const el = textRef.current
    if (!el) return
    setIsClamped(el.scrollHeight > el.clientHeight + 1)
  }, [])

  useEffect(() => {
    checkClamp()
  }, [checkClamp, text])

  // Tailwind JIT cannot detect a dynamically-built `line-clamp-[n]` class name,
  // so the clamp itself is an inline style keyed on `maxLines` instead.
  const clampStyle: React.CSSProperties | undefined = isExpanded
    ? undefined
    : {
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
          {label}
        </Text>
      )}
      <Text
        ref={textRef}
        size="sm"
        className="leading-[1.4] text-twin-chat-muted"
        style={clampStyle}
      >
        {text}
      </Text>
      {(isClamped || isExpanded) && (
        <button
          type="button"
          data-testid="collapsible-text-toggle"
          className="self-start text-sm font-medium text-twin-chat-accent transition-opacity hover:opacity-80"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  )
}
